import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Modal,
  OutlinedInput,
  Select,
  Slider,
  TextField,
  Typography
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { useEffect, useState } from 'react';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Icon } from '@iconify/react';
import { LowBatteryThresholdRange } from '../../../utils/Utilities';
import { ReactComponent as CheckBoxOutlineIcon } from '../../../icons/checkBoxOutlineIcon.svg';
import { ReactComponent as CheckedIcon } from '../../../icons/checkBoxIcon.svg';
import { getStatusColor } from '../../../utils/Parser';
import ConfirmAlertDialog from '../../ConfirmDialog';
import RfidManagementModal from '../../RfidManagementModal';
import SwitchButton from './SwitchButton';
import DeviceActionPowerSetting from './DeviceActionPowerSetting';
import { useAuth } from '../../customContextProviders/AuthContext';
import { DeleteDeviceById, UpdateDeviceDataById } from '../../../network/Devices';

const useStyles = makeStyles((theme) => ({
  customDashCard: {
    background: '#FFFFFF',
    boxShadow: '10px 10px 19px 1px #EAEAFF',
    borderRadius: '20px',
    minHeight: '190px'
  },
  textStyle: {
    fontSize: '12px',
    fontWeight: '600',
    lineHeight: '18px',
    textAlign: 'left',
    color: '#7c7d7d',
    textWrap: 'nowrap'
  },
  textStyleMenuItem: {
    fontSize: '12px',
    fontWeight: '600',
    lineHeight: '18px',
    textAlign: 'left',
    color: '#000000'
  },
  sliderInputFields: {
    minwidth: '80px !important',
    width: 'max-content !important',
    height: '8px !important',
    fontSize: '12px !important'
  },
  spanStyle: {
    color: '#171D40',
    fontWeight: '700',
    textTransform: 'uppercase'
  },
  menuItem: {
    fontSize: '14px',
    [theme.breakpoints.down('sm')]: {
      fontSize: '12px'
    },
    minHeight: '20px',
    borderRadius: '8px',
    marginInline: theme.spacing(1)
  }
}));

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 'max-content',
  minWidth: { xs: '80%', sm: '40%' },
  maxWidth: { xs: '80%', sm: '40%' },
  bgcolor: 'background.paper',
  // border: '1px solid #000',
  boxShadow: '0px 4px 4px 0px #00000040',
  p: 2,
  borderRadius: 1,
  height: '90vh',
  overflow: 'scroll',
  overflowX: 'hidden',
  display: 'flex',
  flexDirection: 'column'
};

export default function DeviceSettingsModal({
  openAssignedModal,
  handleClose,
  deviceDetail,
  onAction,
  setAlertMetas,
  alertMetas
}) {
  const classes = useStyles();
  const { sessionUserData } = useAuth();
  const userRole = sessionUserData?.user?.userRole?.roleName;
  const unlockChannelMethods = ['Dashboard', 'RFID', 'SMS'];
  const [isRfidManagementModal, setIsRfidManagementModal] = useState(false);
  const [deviceSettingData, setDeviceSettingData] = useState({
    delayedTripThreshold: deviceDetail?.device_settings?.delayedTripThreshold,
    isShipmentDelayAllowed: deviceDetail?.device_settings?.isShipmentDelayAllowed,
    geoFenceRadius: deviceDetail?.device_settings?.geoFenceRadius,
    // password: '',
    vipNumbers: deviceDetail?.vipNumbers || [],
    lowBatteryThreshold: deviceDetail?.device_settings?.lowBatteryThreshold,
    notificationFrequency: deviceDetail?.device_settings?.notificationFrequency,
    psm_freq: deviceDetail?.device_settings?.psm_freq,
    selectedUnlockChannel: ['Dashboard', 'RFID', 'SMS'],
    unlock_channel_MQTT: deviceDetail?.device_settings.unlock_channel_MQTT,
    unlock_channel_SMS: deviceDetail?.device_settings.unlock_channel_SMS,
    unlock_channel_RFID: deviceDetail?.device_settings.unlock_channel_RFID,
    isPowerSwitchEnabled: deviceDetail?.device_settings.isPowerSwitchEnabled
  });
  const [openConfirmAlertDialog, setOpenConfirmAlertDialog] = useState(false);
  const [confirmAlertDialogMetas, setConfirmAlertDialogMetas] = useState({
    message: '',
    title: '',
    positiveBtnLable: '',
    negativeBtnLabel: ''
  });
  const [vipNumberError, setVipNumberError] = useState(false);
  const [vipLengthError, setVipLengthError] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setDeviceSettingData({ ...deviceSettingData, [name]: value });
  };

  const handleSlider = (event, newValue) => {
    const { name } = event.target;
    setDeviceSettingData({
      ...deviceSettingData,
      [name]: newValue
    });
  };

  const handleInputChange = (e) => {
    const { value, name } = e.target;
    const parsedValue = Number(value);

    setDeviceSettingData({
      ...deviceSettingData,
      [name]: parsedValue
    });
  };

  const handleBlur = (e) => {
    const { value, name } = e.target;
    const parsedValue = Number(value);

    let min = 0;
    let max = 0;

    if (name === 'delayedTripThreshold') {
      min = 30;
      max = 1440;
    } else if (name === 'psm_freq') {
      min = 5;
      max = 10080;
    } else if (name === 'geoFenceRadius') {
      min = 10;
      max = 10000;
    } else if (name === 'notificationFrequency') {
      min = 30;
      max = 21600;
    }

    // If value is out of range, reset to closest boundary
    if (parsedValue < min) {
      setDeviceSettingData({
        ...deviceSettingData,
        [name]: min
      });
    } else if (parsedValue > max) {
      setDeviceSettingData({
        ...deviceSettingData,
        [name]: max
      });
    }
  };

  const handleSwitchStatus = (e, name) => {
    setDeviceSettingData({ ...deviceSettingData, [name]: e.target.checked });
  };

  const handleSubmit = async () => {
    const payload = {
      name: deviceDetail?.name,
      type: deviceDetail?.type,
      note: deviceDetail?.note,
      settings: {
        delayedTripThreshold: deviceSettingData.delayedTripThreshold,
        isShipmentDelayAllowed: deviceSettingData.isShipmentDelayAllowed,
        geoFenceRadius: deviceSettingData.geoFenceRadius,
        password: 'Proxgy@1234',
        vipNumbers: deviceSettingData.vipNumbers,
        lowBatteryThreshold: deviceSettingData.lowBatteryThreshold,
        notificationFrequency: deviceSettingData.notificationFrequency,
        serialNumber: deviceDetail.device_settings.serialNumber,
        imei: deviceDetail.device_settings.imei,
        fwVersion: deviceDetail.device_settings.fwVersion,
        communicationMode: 'MQTT',
        psm_freq: deviceSettingData.psm_freq,
        unlock_channel_MQTT: deviceSettingData.unlock_channel_MQTT,
        unlock_channel_SMS: deviceSettingData.unlock_channel_SMS,
        unlock_channel_RFID: deviceSettingData.unlock_channel_RFID,
        isPowerSwitchEnabled: deviceSettingData.isPowerSwitchEnabled,
        mqtt_host: 'test',
        mqtt_port: 1883,
        mqtt_username: 'mqttUser',
        mqtt_password: 'mqttPassword'
      }
    };
    try {
      const res = await UpdateDeviceDataById(deviceDetail?.id, payload);
      if (res) {
        setAlertMetas({
          ...alertMetas,
          severity: 'success',
          snackBarOpened: true,
          msg: 'Device Setting Updated Successfully!!'
        });
        setTimeout(() => setAlertMetas({ ...alertMetas, snackBarOpened: false, msg: '' }), 4000);
        handleClose();
        onAction();
      }
    } catch (error) {
      setAlertMetas({
        ...alertMetas,
        severity: 'error',
        snackBarOpened: true,
        msg: error?.response?.data?.message
      });
      setTimeout(() => setAlertMetas({ ...alertMetas, snackBarOpened: false, msg: '' }), 4000);
      onAction();
    }
  };

  const deleteDeviceById = async () => {
    try {
      const res = await DeleteDeviceById(deviceDetail?.id);
      if (res) {
        setAlertMetas({
          ...alertMetas,
          severity: 'error',
          snackBarOpened: true,
          msg: res?.data?.data
        });
        setTimeout(() => setAlertMetas({ ...alertMetas, snackBarOpened: false, msg: '' }), 4000);
        handleClose();
        onAction();
      }
    } catch (error) {
      setAlertMetas({
        ...alertMetas,
        severity: 'error',
        snackBarOpened: true,
        msg: error?.response?.data?.message
      });
      setTimeout(() => setAlertMetas({ ...alertMetas, snackBarOpened: false, msg: '' }), 4000);
      onAction();
    }
  };

  const deleteDevice = () => {
    setConfirmAlertDialogMetas({
      ...confirmAlertDialogMetas,
      message: 'Are you sure want to delete this device ?',
      title: 'Delete',
      positiveBtnLable: 'Confirm',
      negativeBtnLabel: 'Cancel'
    });
    setOpenConfirmAlertDialog(true);
  };

  useEffect(() => {
    if (deviceDetail) {
      /* eslint-disable */
      const {
        delayedTripThreshold,
        isShipmentDelayAllowed,
        geoFenceRadius,
        lowBatteryThreshold,
        notificationFrequency,
        psm_freq,
        unlock_channel_MQTT,
        unlock_channel_SMS,
        unlock_channel_RFID,
        isPowerSwitchEnabled
      } = deviceDetail.device_settings;
      /* eslint-disable */
      const selectedChannels = [];
      if (unlock_channel_MQTT) selectedChannels.push('Dashboard');
      if (unlock_channel_SMS) selectedChannels.push('SMS');
      if (unlock_channel_RFID) selectedChannels.push('RFID');

      setDeviceSettingData({
        delayedTripThreshold: delayedTripThreshold,
        isShipmentDelayAllowed: isShipmentDelayAllowed,
        geoFenceRadius: geoFenceRadius,
        // password: '',
        vipNumbers: deviceDetail?.device_settings.vipNumbers || [],
        lowBatteryThreshold: lowBatteryThreshold,
        notificationFrequency: notificationFrequency,
        psm_freq: psm_freq ? psm_freq : null,
        selectedUnlockChannel: selectedChannels,
        unlock_channel_MQTT: unlock_channel_MQTT,
        unlock_channel_SMS: unlock_channel_SMS,
        unlock_channel_RFID: unlock_channel_RFID,
        isPowerSwitchEnabled: isPowerSwitchEnabled
      });
    }
  }, [deviceDetail]);

  useEffect(() => {
    setDeviceSettingData((prevState) => ({
      ...prevState,
      unlock_channel_RFID: deviceSettingData.selectedUnlockChannel.includes('RFID'),
      unlock_channel_MQTT: deviceSettingData.selectedUnlockChannel.includes('Dashboard'),
      unlock_channel_SMS: deviceSettingData.selectedUnlockChannel.includes('SMS')
    }));
  }, [deviceSettingData?.selectedUnlockChannel]);

  return (
    <Box>
      <Modal
        open={openAssignedModal}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        data-testid="device-settings-modal"
      >
        <Box
          sx={style}
          style={{
            backgroundColor: '#F8FAFB'
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 3
            }}
          >
            <Typography sx={{ fontSize: '15px', fontWeight: 900 }}>Device Settings</Typography>
            {deviceDetail?.sleepStatus && (
              <Typography sx={{ fontSize: '15px', fontWeight: 900, color: '#ff9800' }}>
                Sleep Mode <Icon icon="streamline:sleep" width="22" height="22" />
              </Typography>
            )}
          </Box>

          <Grid container sx={{ gap: 1 }} marginBottom={3}>
            <Grid item xs={12} sm={6} md={6} lg={4}>
              <Typography className={classes.textStyle}>
                Device ID : <span className={classes.spanStyle}>{deviceDetail?.name}</span>
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={6} lg={4}>
              <Typography className={classes.textStyle}>
                IMEI :{' '}
                <span className={classes.spanStyle}>{deviceDetail?.device_settings?.imei}</span>
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={6} lg={4}>
              <Typography className={classes.textStyle}>
                Firmware :{' '}
                <span className={classes.spanStyle}>{deviceDetail?.latestEvent?.version}</span>
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={6} lg={4}>
              <Typography className={classes.textStyle}>
                Device Status :{' '}
                <span
                  className={classes.spanStyle}
                  style={{ color: getStatusColor(deviceDetail?.deviceLiveStatus) }}
                >
                  {' '}
                  {deviceDetail?.deviceLiveStatus}
                </span>
              </Typography>
            </Grid>
            {/* <Grid item xs={12} sm={6} md={6} lg={4}>
              <Typography className={classes.textStyle}>
                SIM 1 : <span className={classes.spanStyle}>Airtel</span>
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={6} lg={4}>
              <Typography className={classes.textStyle}>
                SIM 2 : <span className={classes.spanStyle}>Jio</span>
              </Typography>
            </Grid> */}
          </Grid>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', lg: 'row' },
              justifyContent: 'space-between',
              // alignItems: 'center',
              gap: { xs: '8px', lg: '10px' },
              marginBottom: 2,
              paddingTop: 2,
              borderTop: '1px solid #EAE9EE'
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: { xs: '6px', lg: '10px' },
                alignItems: { xs: 'start', lg: 'center' },
                textWrap: 'nowrap'
              }}
            >
              <DeviceActionPowerSetting
                deviceDetail={deviceDetail}
                setAlertMetas={setAlertMetas}
                alertMetas={alertMetas}
              />
            </Box>

            <Box
              sx={{
                display: 'flex',
                gap: '10px',
                alignItems: { xs: 'start', lg: 'center' }
              }}
            >
              <Button
                onClick={() => setIsRfidManagementModal(true)}
                variant="outlined"
                color="primary"
                size="small"
                sx={{ backgroundColor: '#ffffff', height: '35px', border: '1px solid #EAE9EE' }}
                endIcon={<ArrowForwardIosIcon fontSize="small" />}
              >
                RFID Management
              </Button>
            </Box>
          </Box>

          <Box
            sx={{
              borderTop: '1px solid #EAE9EE',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between'
            }}
          >
            <Grid data-testid="device-setting-grid" container spacing={2}>
              <Grid data-testid="vip-number-grid" item xs={12} lg={5}>
                <FormControl fullWidth sx={{ mt: 2 }}>
                  <Typography className={classes.textStyle}>VIP Number</Typography>
                  <Autocomplete
                    multiple
                    id="tags-filled"
                    options={[]}
                    freeSolo
                    name="vipNumbers"
                    value={deviceSettingData.vipNumbers}
                    onChange={(_, value, reason) => {
                      if (reason === 'remove-option') {
                        setVipNumberError(false);
                        setVipLengthError(false);
                      }

                      const filteredValues = value.filter((val) => /^\d{10}$/.test(val));

                      if (filteredValues.length > 4) {
                        setVipNumberError(true);
                        return;
                      }
                      if (value.length > 0 && !/^\d{10}$/.test(value[value.length - 1])) {
                        setVipLengthError(true);
                        return;
                      } else {
                        setVipLengthError(false);
                      }

                      setVipNumberError(false);
                      setDeviceSettingData({ ...deviceSettingData, vipNumbers: filteredValues });
                    }}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => {
                        const { key, ...tagProps } = getTagProps({ index });
                        return (
                          <Chip
                            variant="outlined"
                            label={option}
                            key={key}
                            {...tagProps}
                            size="small"
                          />
                        );
                      })
                    }
                    renderInput={(params) => (
                      <TextField
                        type="number"
                        {...params}
                        size="small"
                        sx={{ backgroundColor: '#ffffff' }}
                        error={vipNumberError || vipLengthError}
                        helperText={
                          vipNumberError
                            ? 'You can add only up to 4 numbers.'
                            : vipLengthError
                              ? 'Invalid number'
                              : ''
                        }
                      />
                    )}
                  />
                </FormControl>
              </Grid>

              <Grid data-testid="shipment-delay-deviceSetting" item xs={12} lg={7} fullWidth>
                <FormControl fullWidth sx={{ mt: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography className={classes.textStyle} mb={1}>
                      Shipment Delay Allowance
                    </Typography>
                    <SwitchButton
                      inputProps={{ 'aria-label': 'ant design' }}
                      handleChange={(e) => handleSwitchStatus(e, 'isShipmentDelayAllowed')}
                      value={deviceSettingData.isShipmentDelayAllowed}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Typography className={classes.textStyle} mb={1}>
                      30 min
                    </Typography>
                    <Slider
                      min={30}
                      max={1440}
                      name="delayedTripThreshold"
                      size="small"
                      onChange={handleSlider}
                      value={deviceSettingData?.delayedTripThreshold}
                      disabled={!deviceSettingData.isShipmentDelayAllowed}
                      valueLabelDisplay="auto"
                    />
                    <Typography className={classes.textStyle}>1440 min</Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        maxWidth: '120px',
                        flexShrink: 1
                      }}
                    >
                      <TextField
                        variant="standard"
                        size="small"
                        value={deviceSettingData?.delayedTripThreshold}
                        onChange={handleInputChange}
                        disabled={!deviceSettingData.isShipmentDelayAllowed}
                        onBlur={handleBlur}
                        name="delayedTripThreshold"
                        InputProps={{
                          step: 10,
                          min: 30,
                          max: 1440,
                          type: 'number',
                          'aria-labelledby': 'input-slider',
                          endAdornment: (
                            <InputAdornment
                              position="end"
                              sx={{
                                marginLeft: '1px',
                                '& .MuiTypography-root': {
                                  fontSize: '12px',
                                  fontWeight: '600'
                                }
                              }}
                            >
                              min
                            </InputAdornment>
                          ),
                          inputProps: {
                            style: {
                              textAlign: 'right',
                              fontSize: '12px',
                              paddingBottom: '2px'
                            }
                          }
                        }}
                        sx={{
                          minWidth: '40px',
                          maxWidth: '100px',
                          fontSize: '12px',
                          paddingBottom: '3px',
                          transition: 'width 0.2s ease-in-out',
                          flexShrink: 1
                        }}
                      />
                    </Box>
                  </Box>
                </FormControl>
              </Grid>

              <Grid data-testid="unlock-channel-deviceSetting" item xs={12} lg={5}>
                <FormControl fullWidth>
                  <Typography className={classes.textStyle} sx={{ mb: '6px' }}>
                    {' '}
                    Unlock Channel
                  </Typography>
                  <InputLabel id="filter-by" sx={{ color: 'black' }} />
                  <Select
                    id="unlock-channel-multiple-chip"
                    name="selectedUnlockChannel"
                    multiple
                    value={deviceSettingData.selectedUnlockChannel}
                    size="small"
                    sx={{ backgroundColor: '#ffffff', fontSize: '12px' }}
                    onChange={handleChange}
                    IconComponent={ExpandMoreIcon}
                    input={
                      <OutlinedInput id="selectedDevice-multiple-chip" sx={{ fontSize: '12px' }} />
                    }
                    renderValue={(selected) => selected.join(', ')}
                  >
                    {unlockChannelMethods.map((type) => (
                      <MenuItem
                        key={type}
                        value={type}
                        sx={{
                          m: 1,
                          p: 0,
                          '&:hover': {
                            backgroundColor: 'transparent'
                          },
                          '&.Mui-selected': {
                            backgroundColor: 'transparent',
                            '&:hover': {
                              backgroundColor: 'transparent'
                            }
                          },
                          '&:focus': {
                            backgroundColor: 'transparent'
                          },
                          '&.Mui-selected.Mui-focusVisible': {
                            backgroundColor: 'transparent'
                          }
                        }}
                        disableRipple
                        className={classes.menuItem}
                      >
                        <Checkbox
                          checked={deviceSettingData.selectedUnlockChannel.indexOf(type) > -1}
                          size="small"
                          sx={{ color: 'transparent' }}
                          icon={
                            CheckBoxOutlineIcon ? (
                              <CheckBoxOutlineIcon style={{ height: '12px', width: '12px' }} />
                            ) : null
                          }
                          checkedIcon={
                            CheckedIcon ? (
                              <CheckedIcon style={{ height: '12px', width: '12px' }} />
                            ) : null
                          }
                        />
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid data-testid="geofence-radius-deviceSetting" item xs={12} lg={7}>
                <FormControl fullWidth>
                  <Typography className={classes.textStyle} mb={1}>
                    Geofence Radius
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Typography className={classes.textStyle}>10 m</Typography>
                    <Slider
                      min={10}
                      max={10000}
                      name="geoFenceRadius"
                      size="small"
                      onChange={handleSlider}
                      value={deviceSettingData.geoFenceRadius}
                      valueLabelDisplay="auto"
                      disabled={false}
                    />
                    <Typography className={classes.textStyle}>10,000 m</Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        maxWidth: '120px',
                        flexShrink: 1
                      }}
                    >
                      <TextField
                        variant="standard"
                        size="small"
                        value={deviceSettingData?.geoFenceRadius}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        name="geoFenceRadius"
                        InputProps={{
                          step: 10,
                          min: 10,
                          max: 10000,
                          type: 'number',
                          'aria-labelledby': 'input-slider',
                          endAdornment: (
                            <InputAdornment
                              position="end"
                              sx={{
                                marginLeft: '1px',
                                '& .MuiTypography-root': {
                                  fontSize: '12px',
                                  fontWeight: '600'
                                }
                              }}
                            >
                              m
                            </InputAdornment>
                          ),
                          inputProps: {
                            style: {
                              textAlign: 'right',
                              fontSize: '12px',
                              paddingBottom: '2px'
                            }
                          }
                        }}
                        sx={{
                          minWidth: '40px',
                          maxWidth: '100px',
                          fontSize: '12px',
                          paddingBottom: '3px',
                          transition: 'width 0.2s ease-in-out',
                          flexShrink: 1
                        }}
                      />
                    </Box>
                  </Box>
                </FormControl>
              </Grid>

              <Grid data-testid="low-battery-deviceSetting" item xs={12} lg={5}>
                <FormControl fullWidth>
                  <Typography className={classes.textStyle} sx={{ mb: '6px' }}>
                    {' '}
                    Low Battery threshold
                  </Typography>
                  <InputLabel id="filter-by" sx={{ color: 'black' }} />
                  <Select
                    labelId="filter-by"
                    id="filter-by"
                    name="lowBatteryThreshold"
                    size="small"
                    sx={{
                      backgroundColor: '#ffffff',
                      fontSize: '12px'
                    }}
                    value={deviceSettingData?.lowBatteryThreshold}
                    onChange={handleChange}
                    IconComponent={ExpandMoreIcon}
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 180,
                          overflowY: 'auto'
                        }
                      }
                    }}
                  >
                    {LowBatteryThresholdRange?.map((item) => (
                      <MenuItem
                        key={item}
                        className={`${classes.textStyle} ${classes.menuItem}`}
                        value={item}
                      >
                        {item} %
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid data-testid="notification-frequency-deviceSetting" item xs={12} lg={7}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography className={classes.textStyle} mb={1}>
                    Server Data Update Frequency
                  </Typography>
                  {/* <SwitchButton inputProps={{ 'aria-label': 'ant design' }} /> */}
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Typography className={classes.textStyle}>30 sec</Typography>
                  <Slider
                    min={30}
                    max={21600}
                    name="notificationFrequency"
                    size="small"
                    onChange={handleSlider}
                    value={deviceSettingData.notificationFrequency}
                    disabled={false}
                    valueLabelDisplay="auto"
                  />
                  <Typography className={classes.textStyle}>21600 sec</Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      maxWidth: '120px',
                      flexShrink: 1
                    }}
                  >
                    <TextField
                      variant="standard"
                      size="small"
                      value={deviceSettingData.notificationFrequency}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      name="notificationFrequency"
                      InputProps={{
                        step: 10,
                        min: 30,
                        max: 3600,
                        type: 'number',
                        'aria-labelledby': 'input-slider',
                        endAdornment: (
                          <InputAdornment
                            position="end"
                            sx={{
                              marginLeft: '1px',
                              '& .MuiTypography-root': {
                                fontSize: '12px',
                                fontWeight: '600',
                                paddingBottom: '3px'
                              }
                            }}
                          >
                            sec
                          </InputAdornment>
                        ),
                        inputProps: {
                          style: {
                            textAlign: 'right',
                            fontSize: '12px',
                            paddingBottom: '2px'
                          }
                        }
                      }}
                      sx={{
                        minWidth: '40px',
                        maxWidth: '100px',
                        fontSize: '12px',
                        paddingBottom: '3px',
                        transition: 'width 0.2s ease-in-out',
                        flexShrink: 1
                      }}
                    />
                  </Box>
                </Box>
              </Grid>
              {/* 
              <Grid item xs={12} lg={6}>
                <FormControl fullWidth>
                  <Typography className={classes.textStyle} sx={{ mb: '6px' }}>
                    Set Password
                  </Typography>
                  <TextField
                    type={showPassword ? 'text' : 'password'}
                    variant="outlined"
                    placeholder="Enter Password"
                    name="password"
                    size="small"
                    sx={{ backgroundColor: '#ffffff' }}
                    value={deviceSettingData.password}
                    onChange={handleChange}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                            <Icon icon={showPassword ? eyeFill : eyeOffFill} />
                          </IconButton>
                        </InputAdornment>
                      ),
                      inputProps: {
                        style: {
                          fontSize: '12px',
                          height: '24px'
                        }
                      }
                    }}
                  />
                </FormControl>
              </Grid>  */}
              <Grid
                data-testid="switch-bypass-deviceSetting"
                item
                xs={6}
                lg={5}
                sx={{ display: 'flex', gap: '20px', alignItems: 'center' }}
              >
                <Typography className={classes.textStyle}>Enable Power Switch</Typography>
                <SwitchButton
                  inputProps={{ 'aria-label': 'ant design' }}
                  handleChange={(e) => handleSwitchStatus(e, 'isPowerSwitchEnabled')}
                  value={deviceSettingData.isPowerSwitchEnabled}
                />
              </Grid>

              <Grid data-testid="psm-frequency-deviceSetting" item xs={12} lg={7}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography className={classes.textStyle} mb={1}>
                    PSM Data Frequency
                  </Typography>
                  {/* <SwitchButton inputProps={{ 'aria-label': 'ant design' }} /> */}
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Typography className={classes.textStyle}>5 min</Typography>
                  <Slider
                    min={5}
                    max={10080}
                    name="psm_freq"
                    size="small"
                    onChange={handleSlider}
                    value={deviceSettingData.psm_freq}
                    disabled={false}
                    valueLabelDisplay="auto"
                  />
                  <Typography className={classes.textStyle}>10080 min</Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      maxWidth: '120px',
                      flexShrink: 1
                    }}
                  >
                    <TextField
                      variant="standard"
                      size="small"
                      value={deviceSettingData.psm_freq}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      name="psm_freq"
                      InputProps={{
                        step: 10,
                        min: 5,
                        max: 1440,
                        type: 'number',
                        'aria-labelledby': 'input-slider',
                        endAdornment: (
                          <InputAdornment
                            position="end"
                            sx={{
                              marginLeft: '1px',
                              '& .MuiTypography-root': {
                                fontSize: '12px',
                                fontWeight: '600'
                              }
                            }}
                          >
                            min
                          </InputAdornment>
                        ),
                        inputProps: {
                          style: {
                            textAlign: 'right',
                            fontSize: '12px',
                            paddingBottom: '2px'
                          }
                        }
                      }}
                      sx={{
                        minWidth: '40px',
                        maxWidth: '100px',
                        fontSize: '12px',
                        paddingBottom: '3px',
                        transition: 'width 0.2s ease-in-out',
                        flexShrink: 1
                      }}
                    />
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: { xs: 3, lg: 'auto' },
              textWrap: 'nowrap'
            }}
          >
            <Box>
              {userRole.toUpperCase() === 'SUPERADMIN' && (
                <Button
                  variant="contained"
                  size="small"
                  sx={{ backgroundColor: '#C30A0A', boxShadow: 'none', height: '35px' }}
                  onClick={deleteDevice}
                >
                  Delete Device
                </Button>
              )}
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'end', gap: { xs: '4px', lg: '20px' } }}>
              <Button
                variant="outlined"
                color="primary"
                size="small"
                sx={{ boxShadow: 'none', height: '35px', border: '1px solid #EAE9EE' }}
                onClick={handleClose}
              >
                cancel
              </Button>
              <Button
                variant="contained"
                size="small"
                color="primary"
                sx={{ boxShadow: 'none', height: '35px' }}
                onClick={handleSubmit}
              >
                Save
              </Button>
            </Box>
          </Box>
          {isRfidManagementModal && (
            <RfidManagementModal
              openAssignedModal={isRfidManagementModal}
              handleClose={() => setIsRfidManagementModal(false)}
              deviceId={deviceDetail?.id}
              setAlertMetas={setAlertMetas}
              alertMetas={alertMetas}
            />
          )}
          <ConfirmAlertDialog
            openConfirmAlertDialog={openConfirmAlertDialog}
            message={confirmAlertDialogMetas?.message}
            title={confirmAlertDialogMetas?.title}
            positiveBtnLable={confirmAlertDialogMetas?.positiveBtnLable}
            negativeBtnLable={confirmAlertDialogMetas?.negativeBtnLabel}
            handleClose={() => setOpenConfirmAlertDialog(false)}
            handlePositiveClose={() => {
              deleteDeviceById();
              setOpenConfirmAlertDialog(false);
            }}
            handleNegativeClose={() => setOpenConfirmAlertDialog(false)}
          />
        </Box>
      </Modal>
    </Box>
  );
}
